import { Check } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';

export default function ModifierGroup({ 
  group, 
  selectedOptions, 
  onSelectionChange 
}) {
  const isRadio = group.max_selections === 1;
  const isRequired = group.is_required;

  const handleRadioChange = (optionId) => {
    const option = group.options.find(opt => opt.id === parseInt(optionId));
    if (option) {
      onSelectionChange(group.id, [option]);
    }
  };

  const handleCheckboxChange = (option, checked) => {
    let newSelection = [...selectedOptions];
    
    if (checked) {
      // Check if max selections reached
      if (newSelection.length >= group.max_selections) {
        return;
      }
      newSelection.push(option);
    } else {
      newSelection = newSelection.filter(opt => opt.id !== option.id);
    }
    
    onSelectionChange(group.id, newSelection);
  };

  const isSelected = (optionId) => {
    return selectedOptions.some(opt => opt.id === optionId);
  };

  const canSelectMore = selectedOptions.length < group.max_selections;

  return (
    <div className="border-b pb-4">
      {/* Group Header */}
      <div className="mb-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">
            {group.name}
            {isRequired && <span className="text-red-500 ml-1">*</span>}
          </h3>
          {!isRequired && (
            <span className="text-xs text-gray-500">Optional</span>
          )}
        </div>
        
        {/* Selection Rules */}
        <div className="text-xs text-gray-500 mt-1">
          {isRadio ? (
            <span>Select 1</span>
          ) : (
            <span>
              Select {group.min_selections > 0 && `at least ${group.min_selections}`}
              {group.min_selections > 0 && group.max_selections < 999 && ' - '}
              {group.max_selections < 999 && `up to ${group.max_selections}`}
            </span>
          )}
        </div>
      </div>

      {/* Options */}
      {isRadio ? (
        <RadioGroup
          value={selectedOptions[0]?.id?.toString()}
          onValueChange={handleRadioChange}
          className="space-y-2"
        >
          {group.options.map((option) => (
            <div
              key={option.id}
              className={`
                flex items-center justify-between p-3 rounded-lg border-2 transition-all cursor-pointer
                ${isSelected(option.id)
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300 bg-white'
                }
              `}
              onClick={() => handleRadioChange(option.id.toString())}
            >
              <div className="flex items-center gap-3 flex-1">
                <RadioGroupItem value={option.id.toString()} id={`radio-${option.id}`} />
                <Label 
                  htmlFor={`radio-${option.id}`}
                  className="font-medium text-gray-900 cursor-pointer flex-1"
                >
                  {option.name}
                </Label>
              </div>
              <span className="font-semibold text-gray-700">
                {option.price > 0 ? `+$${option.price.toFixed(2)}` : 'Free'}
              </span>
            </div>
          ))}
        </RadioGroup>
      ) : (
        <div className="space-y-2">
          {group.options.map((option) => {
            const selected = isSelected(option.id);
            const disabled = !selected && !canSelectMore;
            
            return (
              <div
                key={option.id}
                className={`
                  flex items-center justify-between p-3 rounded-lg border-2 transition-all
                  ${selected
                    ? 'border-blue-500 bg-blue-50'
                    : disabled
                    ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                    : 'border-gray-200 hover:border-gray-300 bg-white cursor-pointer'
                  }
                `}
                onClick={() => !disabled && handleCheckboxChange(option, !selected)}
              >
                <div className="flex items-center gap-3 flex-1">
                  <Checkbox
                    id={`checkbox-${option.id}`}
                    checked={selected}
                    disabled={disabled}
                    onCheckedChange={(checked) => handleCheckboxChange(option, checked)}
                  />
                  <Label 
                    htmlFor={`checkbox-${option.id}`}
                    className={`font-medium cursor-pointer flex-1 ${disabled ? 'text-gray-400' : 'text-gray-900'}`}
                  >
                    {option.name}
                  </Label>
                </div>
                <span className={`font-semibold ${disabled ? 'text-gray-400' : 'text-gray-700'}`}>
                  {option.price > 0 ? `+$${option.price.toFixed(2)}` : 'Free'}
                </span>
              </div>
            );
          })}
          
          {/* Selection Counter */}
          {group.max_selections < 999 && (
            <div className="text-xs text-gray-500 text-right mt-2">
              {selectedOptions.length} of {group.max_selections} selected
            </div>
          )}
        </div>
      )}
    </div>
  );
}
